import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Sparkles, Heart, ChevronLeft, ChevronRight, Users, GraduationCap, HandHeart, BookOpen, Award } from 'lucide-react';
import { api } from '../lib/api';

const ICON_MAP = { Heart, GraduationCap, HandHeart, Users, Sparkles, BookOpen, Award };
const Icon = (name) => ICON_MAP[name] || Heart;

// Build a slide object from DB shape (flat) into the rich UI shape
function adaptDbSlide(s) {
  return {
    id: s.id,
    badge: s.badge,
    badgeText: s.badge_text,
    titleHtml: s.title_html,
    desc: s.description,
    image: s.image,
    primaryCta: { to: s.primary_cta_to, label: s.primary_cta_label, icon: Icon(s.primary_cta_icon) },
    secondaryCta: { to: s.secondary_cta_to, label: s.secondary_cta_label },
    tertiaryCta: { to: s.tertiary_cta_to, label: s.tertiary_cta_label },
    stat: { kicker: s.stat_kicker, value: s.stat_value, progress: s.stat_progress, note: s.stat_note, icon: Icon(s.stat_icon), tone: s.stat_tone },
    quote: { text: s.quote_text, author: s.quote_author },
    pill: { value: s.pill_value, label: s.pill_label, avatars: s.pill_avatars },
  };
}

// Default mock slides — used when DB is empty (initial install)
const DEFAULT_SLIDES = [
  {
    id: 'donate',
    badge: '1992',
    badgeText: '33+ yıldır eğitim için çalışıyoruz',
    title: (
      <>
        <span className="italic text-coral-300 font-light">“Bir Çocuk</span><br />
        Daha Okusun<br />
        <span className="relative inline-block">
          Diye…”
          <svg className="absolute -bottom-3 left-0 w-full" viewBox="0 0 280 20" fill="none" preserveAspectRatio="none" aria-hidden>
            <path d="M2 12 Q 70 2, 140 10 T 278 8" stroke="#C9A96E" strokeWidth="4" strokeLinecap="round" />
          </svg>
        </span>
      </>
    ),
    desc: 'Üstlendiğimiz misyon doğrultusunda eğitimin her kademesinden çocuk ve gencimize karşılıksız eğitim bursları sağlıyor; kültürel, sosyal ve eğitsel projelerimizle onların eğitim hayatında fark yaratıyoruz.',
    image: 'https://images.pexels.com/photos/31367492/pexels-photo-31367492.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1600',
    primaryCta: { to: '/bagis', label: 'Bağış Yap', icon: Heart },
    secondaryCta: { to: '/burslar', label: 'Burs Başvurusu' },
    tertiaryCta: { to: '/gonullu', label: 'Gönüllü Ol' },
    stat: { kicker: 'Bu ay', value: '₺ 2.340.000', progress: 72, note: '%72 hedefimize ulaştık', icon: Heart, tone: 'coral' },
    quote: { text: 'Bu burs benim için sadece maddi değil; bir aidiyet.', author: 'Elif, Tıp Fakültesi' },
    pill: { value: '12.500+ Bursiyer', label: 'Hayatına dokunduğumuz öğrenciler', avatars: true },
  },
  {
    id: 'scholarship',
    badge: '2026',
    badgeText: 'Burs başvuruları başladı',
    title: (
      <>
        Hayalleri <span className="italic text-coral-300 font-light">kanatlandıran</span><br />
        burslar.
      </>
    ),
    desc: 'Üniversite ve lise öğrencilerimize akademik başarı esaslı, tam karşılıksız burs imkânı sunuyoruz. Bursiyerlerimize sadece maddi değil; akademik mentörlük ve kültürel gelişim de sağlıyoruz.',
    image: 'https://images.pexels.com/photos/28927920/pexels-photo-28927920.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1600',
    primaryCta: { to: '/burslar', label: 'Hemen Başvur', icon: GraduationCap },
    secondaryCta: { to: '/projeler?tab=etkinlik', label: 'Etkinliklerimiz' },
    tertiaryCta: { to: '/kurumsal/baskanlar', label: 'Hikâyemiz' },
    stat: { kicker: 'Bu yıl', value: '850+ Bursiyer', progress: 88, note: '%88 başvuru hedefi tamamlandı', icon: GraduationCap, tone: 'gold' },
    quote: { text: 'YEKÜV bursu olmadan üniversiteye gidemezdim. Şimdi mühendis olma yolundayım.', author: 'Mert, İTÜ Bilgisayar Müh.' },
    pill: { value: '03 Burs Türü', label: 'Lise · Lisans · Yüksek Lisans', avatars: false },
  },
  {
    id: 'volunteer',
    badge: 'Topluluk',
    badgeText: 'Gönüllü ailemize katılın',
    title: (
      <>
        Birlikte <span className="italic text-coral-300 font-light">daha güçlüyüz</span>,<br />
        <span className="relative inline-block">
          el ele.
          <svg className="absolute -bottom-3 left-0 w-full" viewBox="0 0 200 20" fill="none" preserveAspectRatio="none" aria-hidden>
            <path d="M2 12 Q 50 2, 100 10 T 198 8" stroke="#C9A96E" strokeWidth="4" strokeLinecap="round" />
          </svg>
        </span>
      </>
    ),
    desc: 'Mentörlük, etkinlik organizasyonu, dijital iletişim ve bağışçı ilişkileri alanlarında yüzlerce gönüllümüzle birlikte çalışıyoruz. Siz de YEKÜV ailesinin bir parçası olabilirsiniz.',
    image: 'https://images.pexels.com/photos/33837414/pexels-photo-33837414.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1600',
    primaryCta: { to: '/gonullu', label: 'Gönüllü Ol', icon: HandHeart },
    secondaryCta: { to: '/projeler?tab=devam', label: 'Projelerimiz' },
    tertiaryCta: { to: '/iletisim', label: 'İletişim' },
    stat: { kicker: 'Aktif gönüllü', value: '500+ Kişi', progress: 65, note: '4 farklı çalışma grubu', icon: Users, tone: 'gold' },
    quote: { text: 'Gönüllü olmak, hayatımdaki en anlamlı karardı. Çocukların gözündeki ışık her şeye değer.', author: 'Selin, Mentör Gönüllüsü' },
    pill: { value: '500+ Gönüllü', label: 'Aktif çalışma topluluğumuz', avatars: true },
  },
];

const AUTO_INTERVAL = 7000;

export default function Hero() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [slides, setSlides] = useState(DEFAULT_SLIDES);

  // Try to load slides from DB; fallback to default mock
  useEffect(() => {
    api.publicHeroSlides()
      .then(rows => {
        if (Array.isArray(rows) && rows.length > 0) {
          setSlides(rows.map(adaptDbSlide));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), AUTO_INTERVAL);
    return () => clearInterval(t);
  }, [paused, slides.length]);

  const slide = slides[idx % slides.length];
  const PrimaryIcon = slide.primaryCta.icon;
  const StatIcon = slide.stat.icon;

  const next = () => setIdx((i) => (i + 1) % slides.length);
  const prev = () => setIdx((i) => (i - 1 + slides.length) % slides.length);

  return (
    <section
      className="relative bg-cream overflow-hidden"
      data-testid="hero-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Decorative grid */}
      <div className="absolute inset-0 opacity-[0.35] pointer-events-none" style={{ backgroundImage: 'linear-gradient(to right, rgba(15,30,77,.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(15,30,77,.06) 1px, transparent 1px)', backgroundSize: '64px 64px', maskImage: 'radial-gradient(ellipse at 50% 30%, black 40%, transparent 75%)' }} />
      <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-gold/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-coral/10 blur-3xl pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-10 md:pt-16 pb-20 md:pb-28 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        {/* Text block — keyed to remount + animate on slide change */}
        <div key={`text-${slide.id}`} className="lg:col-span-6">
          <div className="inline-flex items-center gap-2 bg-white border border-ink-100 rounded-full pl-2 pr-4 py-1.5 shadow-sm fade-up" data-testid={`hero-badge-${slide.id}`}>
            <span className="bg-gold text-ink-700 text-[10px] font-bold tracking-widest uppercase rounded-full px-2 py-1">{slide.badge}</span>
            <span className="text-ink-500 text-xs">{slide.badgeText}</span>
            <Sparkles className="w-3.5 h-3.5 text-gold" />
          </div>

          <h1 className="font-display text-[44px] leading-[1.02] md:text-6xl lg:text-[82px] lg:leading-[0.95] mt-6 text-ink-600 fade-up delay-1 tracking-tight">
            {slide.titleHtml ? <span dangerouslySetInnerHTML={{ __html: slide.titleHtml }} /> : slide.title}
          </h1>

          <p className="mt-8 text-ink-500 text-base md:text-lg leading-relaxed max-w-xl fade-up delay-2">
            {slide.desc}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-3 fade-up delay-3">
            <Link to={slide.primaryCta.to} className="group inline-flex items-center gap-2 bg-ink-600 text-cream px-6 h-14 rounded-full font-semibold hover:bg-ink-500 transition-colors shadow-xl shadow-ink-900/20" data-testid={`hero-primary-cta-${slide.id}`}>
              <PrimaryIcon className={`w-4 h-4 ${slide.id === 'donate' ? 'fill-coral-300 text-coral-300' : 'text-gold'}`} /> {slide.primaryCta.label}
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link to={slide.secondaryCta.to} className="inline-flex items-center gap-2 bg-transparent border border-ink-200 text-ink-600 px-6 h-14 rounded-full font-medium hover:border-ink-500 hover:bg-ink-50/60 transition-colors">
              {slide.secondaryCta.label}
            </Link>
            <Link to={slide.tertiaryCta.to} className="hidden md:inline-flex items-center gap-2 text-ink-500 px-4 h-14 rounded-full font-medium hover:text-ink-600 underline decoration-gold underline-offset-[8px] decoration-2">
              {slide.tertiaryCta.label}
            </Link>
          </div>

          {/* Pill */}
          <div className="mt-12 flex items-center gap-6 fade-up delay-4">
            {slide.pill.avatars && (
              <div className="flex -space-x-3">
                {[1,2,3,4].map(i => (
                  <div key={i} className="w-10 h-10 rounded-full border-2 border-cream overflow-hidden ring-1 ring-ink-100">
                    <img src={`https://i.pravatar.cc/80?img=${i + (slide.id === 'volunteer' ? 30 : 10)}`} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            )}
            {!slide.pill.avatars && (
              <div className="w-12 h-12 rounded-2xl bg-gold/15 grid place-items-center text-gold-500"><GraduationCap className="w-5 h-5" /></div>
            )}
            <div className="text-sm">
              <div className="font-semibold text-ink-600">{slide.pill.value}</div>
              <div className="text-ink-400 text-xs">{slide.pill.label}</div>
            </div>
          </div>
        </div>

        {/* Image composition — also keyed to retrigger transitions */}
        <div className="lg:col-span-6 relative" key={`img-${slide.id}`}>
          <div className="relative aspect-[4/5] md:aspect-[5/6] rounded-[32px] overflow-hidden shadow-2xl shadow-ink-900/20 bg-ink-600">
            {/* All images rendered, fade between */}
            {slides.map((s, i) => (
              <img
                key={s.id}
                src={s.image}
                alt={`Slide ${i + 1}`}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${idx === i ? 'opacity-100' : 'opacity-0'}`}
              />
            ))}
            <div className="absolute inset-0 bg-gradient-to-t from-ink-900/50 via-transparent to-transparent" />
          </div>

          {/* Floating stat card */}
          <div className="absolute -left-4 md:-left-10 bottom-8 md:bottom-14 bg-white rounded-2xl shadow-xl border border-ink-100 p-5 max-w-[260px] fade-up delay-2" data-testid={`hero-stat-${slide.id}`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full grid place-items-center ${slide.stat.tone === 'coral' ? 'bg-coral-50 text-coral-300' : 'bg-gold/15 text-gold-500'}`}>
                <StatIcon className={`w-5 h-5 ${slide.stat.tone === 'coral' ? 'fill-coral-300' : ''}`} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-ink-400">{slide.stat.kicker}</div>
                <div className="font-display font-bold text-xl text-ink-600 tabular">{slide.stat.value}</div>
              </div>
            </div>
            <div className="mt-3 h-1.5 bg-ink-50 rounded-full overflow-hidden">
              <div className="h-full bg-gold rounded-full transition-[width] duration-1000" style={{ width: `${slide.stat.progress}%` }} />
            </div>
            <div className="mt-2 text-xs text-ink-400">{slide.stat.note}</div>
          </div>

          {/* Floating quote */}
          <div className="hidden md:block absolute -right-4 top-10 bg-ink-600 text-cream rounded-2xl p-5 max-w-[240px] shadow-xl fade-up delay-3" data-testid={`hero-quote-${slide.id}`}>
            <div className="text-[10px] uppercase tracking-widest text-gold mb-2">Bursiyer</div>
            <p className="font-display text-[15px] leading-snug italic">"{slide.quote.text}"</p>
            <div className="mt-3 text-xs text-cream/70">— {slide.quote.author}</div>
          </div>
        </div>
      </div>

      {/* Slider controls — full-width bottom strip */}
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pb-10 -mt-6 md:-mt-2">
        <div className="flex items-center justify-between gap-4">
          {/* Slide indicators with progress */}
          <div className="flex items-center gap-3" data-testid="hero-indicators">
            {slides.map((s, i) => (
              <button
                key={s.id}
                onClick={() => setIdx(i)}
                aria-label={`Slayt ${i + 1}`}
                data-testid={`hero-dot-${i}`}
                className="group flex items-center gap-2 py-2"
              >
                <span className={`relative h-1 rounded-full overflow-hidden transition-all ${idx === i ? 'w-16 md:w-24 bg-ink-100' : 'w-8 bg-ink-100 group-hover:bg-ink-200'}`}>
                  {idx === i && !paused && (
                    <span className="absolute inset-y-0 left-0 bg-gold animate-hero-progress" style={{ animationDuration: `${AUTO_INTERVAL}ms` }} />
                  )}
                  {idx === i && paused && (
                    <span className="absolute inset-y-0 left-0 right-0 bg-gold" />
                  )}
                </span>
                <span className={`text-[11px] tracking-[0.25em] uppercase font-bold tabular transition-colors ${idx === i ? 'text-ink-600' : 'text-ink-300 group-hover:text-ink-500'}`}>
                  0{i + 1}
                </span>
              </button>
            ))}
          </div>

          {/* Prev / Next */}
          <div className="flex items-center gap-2">
            <button
              onClick={prev}
              aria-label="Önceki"
              data-testid="hero-prev"
              className="w-11 h-11 rounded-full border border-ink-200 grid place-items-center text-ink-600 hover:bg-ink-600 hover:text-cream hover:border-ink-600 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={next}
              aria-label="Sonraki"
              data-testid="hero-next"
              className="w-11 h-11 rounded-full bg-ink-600 text-cream grid place-items-center hover:bg-ink-500 transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes hero-progress {
          from { width: 0%; }
          to { width: 100%; }
        }
        .animate-hero-progress { animation: hero-progress linear forwards; }
      `}</style>
    </section>
  );
}
